import { useMemo, useState } from 'react';
import { type MessageComposerState, type TranslationLanguages } from 'stream-chat';
import {
  Avatar as DefaultAvatar,
  Attachment as DefaultAttachment,
  QuotedMessagePreviewProps,
  useChatContext,
  useComponentContext,
  useTranslationContext,
  useMessageComposer,
  useStateStore,
  Poll,
} from 'stream-chat-react';

import { pickMessageRenderer } from '../../pick-message-renderer';

const MAX_MESSAGE_LENGTH_BEFORE_TRUNCATION = 250;

const messageComposerStateStoreSelector = (state: MessageComposerState) => ({
  quotedMessage: state.quotedMessage,
});

export const CustomQuotedMessagePreview = ({ renderText }: QuotedMessagePreviewProps) => {
  const { client } = useChatContext();
  const { Attachment = DefaultAttachment, Avatar = DefaultAvatar } =
    useComponentContext('QuotedMessagePreview');
  const { userLanguage } = useTranslationContext('QuotedMessagePreview');
  const messageComposer = useMessageComposer();
  const { quotedMessage } = useStateStore(messageComposer.state, messageComposerStateStoreSelector);
  const [showFullMessage, setShowFullMessage] = useState(false);

  const quotedMessageText = useMemo(() => {
    const text =
      quotedMessage?.i18n?.[`${userLanguage}_text` as `${TranslationLanguages}_text`] ||
      quotedMessage?.text;

    if (!text) return '';

    // ff showing full message or text is short enough, return as-is
    if (showFullMessage || text.length <= MAX_MESSAGE_LENGTH_BEFORE_TRUNCATION) {
      return text;
    }

    return `${text.substring(0, MAX_MESSAGE_LENGTH_BEFORE_TRUNCATION)}...`;
  }, [quotedMessage?.i18n, quotedMessage?.text, userLanguage, showFullMessage]);

  // Falls back to the author-aware renderer rather than Stream's default, which
  // parses a client's prose as markdown and so disagreed with the bubble even
  // before IP-2192.
  const renderedText = useMemo(() => {
    const render = renderText ?? pickMessageRenderer(quotedMessage?.user ?? undefined, client.userID);
    return render(quotedMessageText, quotedMessage?.mentioned_users);
  }, [client.userID, quotedMessageText, quotedMessage?.mentioned_users, quotedMessage?.user, renderText]);

  const quotedMessageAttachments = useMemo(
    () => (quotedMessage?.attachments?.length ? quotedMessage.attachments.slice(0, 1) : []),
    [quotedMessage]
  );

  const poll = quotedMessage?.poll_id && client.polls.fromState(quotedMessage.poll_id);

  if (!quotedMessageText && !quotedMessageAttachments.length && !poll) return null;

  return (
    <div className="str-chat__quoted-message-preview" data-testid="quoted-message-preview">
      <div className="flex flex-col">
        <div className="bg-slate-100 rounded-lg p-2 mb-1">
          {poll ? (
            <Poll isQuoted poll={poll} />
          ) : (
            <>
              {!!quotedMessageAttachments.length && (
                <Attachment attachments={quotedMessageAttachments} isQuoted />
              )}
              <div
                data-testid="quoted-message-text"
                className="p-2 max-h-[250px] overflow-y-auto z-[9999999] whitespace-pre-wrap"
              >
                {renderedText}{' '}
                {quotedMessage?.text &&
                  quotedMessage.text.length > MAX_MESSAGE_LENGTH_BEFORE_TRUNCATION && (
                    <span
                      className="text-blue-500 cursor-pointer text-xs hover:underline -ml-1"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowFullMessage(!showFullMessage);
                      }}
                    >
                      {showFullMessage ? 'Show less' : 'View full message'}
                    </span>
                  )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {quotedMessage?.user && (
            <Avatar
              className="str-chat__avatar--quoted-message-sender"
              image={quotedMessage.user.image}
              name={quotedMessage.user.name || quotedMessage.user.id}
              user={quotedMessage.user}
            />
          )}
        </div>
      </div>
    </div>
  );
};
